import { Box, ButtonBS, Center, Heading, Stack, Text, VStack } from "@biblioteksentralen/react";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { DefaultContainer } from "../../ContentContainer";
import { EventsList } from "../../events/EventsList";
import BlockContent from "../../blockContent/BlockContent";
import { Edit } from "../../editInSanity/EditInSanity";
import { SanityImageWithResponsiveSize } from "../../images/SanityImageWithResponsiveSize";
import { CampaignViewSection } from "../CampaignViewSection";
import { useCampaignEvents } from "../useCampaignEvents";
import CustomCampaignDate from "./CustomCampaignDate";
import { ResolvedCustomCampaign } from "./sanityQuery";
import { useRelevantPublicationsHeading } from "../../lists/utils";
import ItemGrid from "../../../searchAndCollection/components/works/ItemGrid";

type CustomCampaignViewProps = { customCampaign: ResolvedCustomCampaign };

export const CustomCampaignView = ({ customCampaign }: CustomCampaignViewProps) => {
  const { ts } = useTranslation();
  const relevantPublicationsHeading = useRelevantPublicationsHeading(customCampaign.relevantPublications);

  return (
    <DefaultContainer>
      <Stack direction="column" gap="1.5rem">
        <Stack direction="column">
          <CustomCampaignDate campaign={customCampaign} fontWeight="600" fontSize=".8rem" />
          <Heading as="h1" fontSize="4xl">
            {ts(customCampaign.title)}
          </Heading>
          <Edit doc={customCampaign} />
          <Text fontSize="lg" maxWidth="30em">
            {ts(customCampaign.teaser)}
          </Text>
        </Stack>
        {customCampaign.image?.asset && (
          <Box paddingBottom="2rem">
            <SanityImageWithResponsiveSize
              image={customCampaign.image}
              aspectRatio={1.95}
              resolution={{ base: 540, sm: 1080, lg: 2160 }}
            />
          </Box>
        )}
      </Stack>
      <VStack gap={{ base: "3rem", sm: "4rem" }} style={{ hyphens: "none" }}>
        <CampaignViewSection>
          <Box width="100%">
            <BlockContent blocks={ts(customCampaign.description)} />
          </Box>
        </CampaignViewSection>

        <Events events={customCampaign.events} />

        <CampaignViewSection>
          <Box width="100%">
            <BlockContent blocks={ts(customCampaign.body)} />
          </Box>
        </CampaignViewSection>

        {!!customCampaign.relevantPublications?.length && (
          <CampaignViewSection headline={relevantPublicationsHeading}>
            <ItemGrid items={customCampaign.relevantPublications} />
          </CampaignViewSection>
        )}
      </VStack>
    </DefaultContainer>
  );
};

const Events = (props: { events: ResolvedCustomCampaign["events"] }) => {
  const { t } = useTranslation();
  const { events, showingAllEvents, toggleAllEvents, haveAdditionalEvents } = useCampaignEvents(props?.events);

  if (!events?.length) return null;

  return (
    <CampaignViewSection headline={t("Kom på våre arrangementer")}>
      <DefaultContainer>
        <EventsList events={events} />
      </DefaultContainer>
      {haveAdditionalEvents && (
        <Center width="100%" zIndex="1">
          <ButtonBS onClick={toggleAllEvents} variant="secondary">
            {showingAllEvents ? t("Vis færre") : t("Vis alle")}
          </ButtonBS>
        </Center>
      )}
    </CampaignViewSection>
  );
};
